import React, { useState, useEffect } from 'react';
import { useToast } from '../App';
import api from '../utils/api';
import LoadingState from '../components/LoadingState';

const Reviews = () => {
    const { showSuccess, showError } = useToast();
    const [reviews, setReviews] = useState([]);
    const [allReviews, setAllReviews] = useState([]); // Store all reviews for counts
    const [loading, setLoading] = useState(true);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [expandedReview, setExpandedReview] = useState(null);
    const [viewingReview, setViewingReview] = useState(null);
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [deleteModal, setDeleteModal] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage] = useState(10);
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [actionMenu, setActionMenu] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [ratingFilter, setRatingFilter] = useState('all');
    const [productFilter, setProductFilter] = useState('all');
    const [settings, setSettings] = useState({
        reviews_enabled: false,
        show_verified_badge: false,
        verified_only: false,
        enable_star_rating: false,
        star_rating_required: false,
    });

    useEffect(() => {
        loadAllReviews();
        loadSettings();
    }, []);

    useEffect(() => {
        filterReviews();
    }, [filter, allReviews, searchQuery, ratingFilter, productFilter]);

    const loadAllReviews = async () => {
        try {
            setLoading(true);
            const response = await api.getReviews({ status: 'all' });
            if (response.success) {
                setAllReviews(response.reviews || []);
            }
        } catch (error) {
            showError('Failed to load reviews');
        } finally {
            setLoading(false);
        }
    };

    const filterReviews = () => {
        let filtered = allReviews;
        
        // Status filter
        if (filter !== 'all') {
            filtered = filtered.filter(r => r.status === filter);
        }
        
        // Search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(r => 
                r.author_name.toLowerCase().includes(query) ||
                r.author_email.toLowerCase().includes(query) ||
                r.content.toLowerCase().includes(query) ||
                r.product_name.toLowerCase().includes(query)
            );
        }
        
        // Rating filter
        if (ratingFilter !== 'all') {
            filtered = filtered.filter(r => parseInt(r.rating) === parseInt(ratingFilter));
        }
        
        // Product filter
        if (productFilter !== 'all') {
            filtered = filtered.filter(r => String(r.product_id) === String(productFilter));
        }
        
        setReviews(filtered);
        setPage(1); // Reset to first page when filters change
    };

    const loadSettings = async () => {
        try {
            setSettingsLoading(true);
            const response = await api.getReviewSettings();
            if (response.success) {
                setSettings(response.settings);
            }
        } catch (error) {
            console.error('Failed to load settings');
        } finally {
            setSettingsLoading(false);
        }
    };

    const handleStatusChange = async (reviewId, newStatus) => {
        try {
            const response = await api.updateReviewStatus(reviewId, newStatus);
            if (response.success) {
                // Update the review in allReviews
                setAllReviews(prev => prev.map(r => 
                    r.id === reviewId ? { ...r, status: newStatus } : r
                ));
                showSuccess(`Review ${newStatus} successfully`);
            }
        } catch (error) {
            showError('Failed to update review status');
        }
    };

    const handleDelete = async (reviewId) => {
        try {
            const response = await api.deleteReview(reviewId);
            if (response.success) {
                // Remove from allReviews
                setAllReviews(prev => prev.filter(r => r.id !== reviewId));
                setDeleteModal(null);
                showSuccess('Review deleted successfully');
            }
        } catch (error) {
            showError('Failed to delete review');
        }
    };

    const handleReply = async (reviewId) => {
        if (!replyText.trim()) {
            showError('Please enter a reply');
            return;
        }

        try {
            const response = await api.replyToReview(reviewId, replyText);
            if (response.success) {
                showSuccess('Reply posted successfully');
                setReplyingTo(null);
                setReplyText('');
            }
        } catch (error) {
            showError('Failed to post reply');
        }
    };

    const handleSettingChange = async (setting, value) => {
        try {
            const response = await api.updateReviewSettings({ [setting]: value });
            if (response.success) {
                setSettings(prev => ({ ...prev, [setting]: value }));
                showSuccess('Setting updated successfully');
            }
        } catch (error) {
            showError('Failed to update setting');
        }
    };

    const getStatusCounts = () => {
        const counts = {
            all: allReviews.length,
            approved: allReviews.filter(r => r.status === 'approved').length,
            pending: allReviews.filter(r => r.status === 'hold').length,
            spam: allReviews.filter(r => r.status === 'spam').length,
            trash: allReviews.filter(r => r.status === 'trash').length,
        };
        return counts;
    };

    const getUniqueProducts = () => {
        const products = {};
        allReviews.forEach(review => {
            if (!products[review.product_id]) {
                products[review.product_id] = {
                    id: review.product_id,
                    name: review.product_name
                };
            }
        });
        return Object.values(products).sort((a, b) => a.name.localeCompare(b.name));
    };

    const resetFilters = () => {
        setSearchQuery('');
        setRatingFilter('all');
        setProductFilter('all');
        setFilter('all');
    };

    const hasActiveFilters = searchQuery || ratingFilter !== 'all' || productFilter !== 'all';

    const counts = getStatusCounts();
    const uniqueProducts = getUniqueProducts();

    const renderStars = (rating) => {
        return (
            <span className="star-rating">
                {[1, 2, 3, 4, 5].map(star => (
                    <span key={star} className={star <= rating ? 'star filled' : 'star empty'}>
                        ★
                    </span>
                ))}
            </span>
        );
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('desc');
        }
    };

    const getSortedReviews = () => {
        const sorted = [...reviews].sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];
            
            if (sortBy === 'date') {
                aVal = new Date(a.date).getTime();
                bVal = new Date(b.date).getTime();
            }
            
            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });
        
        const start = (page - 1) * perPage;
        const end = start + perPage;
        return sorted.slice(start, end);
    };

    const totalPages = Math.ceil(reviews.length / perPage);
    const paginatedReviews = getSortedReviews();

    // Show loader while checking settings
    if (settingsLoading) {
        return (
            <div className="page reviews-page">
                <div className="page-container">
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <LoadingState message="Loading..." />
                    </div>
                </div>
            </div>
        );
    }

    if (!settings.reviews_enabled) {
        return (
            <div className="page reviews-page">
                <div className="page-container">
                    <div className="page-header">
                        <div className="page-title">
                            <h1>Reviews Management</h1>
                            <p>Manage product reviews and ratings</p>
                        </div>
                    </div>

                    <div className="reviews-disabled-notice">
                        <div className="notice-icon">⚠️</div>
                        <h3>Product Reviews are Disabled</h3>
                        <p>To use the Reviews Management feature, you need to enable product reviews in WooCommerce settings.</p>
                        
                        <div className="enable-instructions">
                            <h4>How to Enable Product Reviews:</h4>
                            <ol>
                                <li>Go to <strong>WooCommerce → Settings</strong></li>
                                <li>Click on the <strong>Products</strong> tab</li>
                                <li>Check the box for <strong>"Enable product reviews"</strong></li>
                                <li>Click <strong>Save changes</strong></li>
                                <li>Refresh this page</li>
                            </ol>
                        </div>

                        <div className="quick-enable">
                            <h4>Quick Enable:</h4>
                            <button 
                                className="btn btn-primary"
                                onClick={() => handleSettingChange('reviews_enabled', true)}
                            >
                                Enable Product Reviews Now
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page reviews-page">
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <h1>Reviews Management</h1>
                        <p>Manage product reviews and ratings</p>
                    </div>
                </div>

                {/* Review Settings */}
                <div className="review-settings-card">
                    <h3>Review Settings</h3>
                    <div className="settings-grid">
                        <div className="setting-item">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={settings.reviews_enabled}
                                    onChange={(e) => handleSettingChange('reviews_enabled', e.target.checked)}
                                />
                                <span>Enable product reviews</span>
                            </label>
                        </div>
                        <div className="setting-item">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={settings.show_verified_badge}
                                    onChange={(e) => handleSettingChange('show_verified_badge', e.target.checked)}
                                />
                                <span>Show "verified owner" label on customer reviews</span>
                            </label>
                        </div>
                        <div className="setting-item">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={settings.verified_only}
                                    onChange={(e) => handleSettingChange('verified_only', e.target.checked)}
                                />
                                <span>Reviews can only be left by "verified owners"</span>
                            </label>
                        </div>
                        <div className="setting-item">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={settings.enable_star_rating}
                                    onChange={(e) => handleSettingChange('enable_star_rating', e.target.checked)}
                                />
                                <span>Enable star rating on reviews</span>
                            </label>
                        </div>
                        <div className="setting-item">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={settings.star_rating_required}
                                    onChange={(e) => handleSettingChange('star_rating_required', e.target.checked)}
                                    disabled={!settings.enable_star_rating}
                                />
                                <span>Star ratings should be required, not optional</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="review-filters">
                    <button
                        className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        All <span className="count-badge badge-all">{counts.all}</span>
                    </button>
                    <button
                        className={`filter-tab ${filter === 'hold' ? 'active' : ''}`}
                        onClick={() => setFilter('hold')}
                    >
                        Pending <span className="count-badge badge-pending">{counts.pending}</span>
                    </button>
                    <button
                        className={`filter-tab ${filter === 'approved' ? 'active' : ''}`}
                        onClick={() => setFilter('approved')}
                    >
                        Approved <span className="count-badge badge-approved">{counts.approved}</span>
                    </button>
                    <button
                        className={`filter-tab ${filter === 'spam' ? 'active' : ''}`}
                        onClick={() => setFilter('spam')}
                    >
                        Spam <span className="count-badge badge-spam">{counts.spam}</span>
                    </button>
                    <button
                        className={`filter-tab ${filter === 'trash' ? 'active' : ''}`}
                        onClick={() => setFilter('trash')}
                    >
                        Trash <span className="count-badge badge-trash">{counts.trash}</span>
                    </button>
                </div>

                {/* Search and Advanced Filters */}
                <div className="reviews-search-filters">
                    <div className="search-box">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            placeholder="Search reviews..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                        {searchQuery && (
                            <button 
                                className="clear-search"
                                onClick={() => setSearchQuery('')}
                                title="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    <select
                        value={ratingFilter}
                        onChange={(e) => setRatingFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Ratings</option>
                        <option value="5">⭐⭐⭐⭐⭐ 5 Stars</option>
                        <option value="4">⭐⭐⭐⭐ 4 Stars</option>
                        <option value="3">⭐⭐⭐ 3 Stars</option>
                        <option value="2">⭐⭐ 2 Stars</option>
                        <option value="1">⭐ 1 Star</option>
                    </select>

                    <select
                        value={productFilter}
                        onChange={(e) => setProductFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Products</option>
                        {uniqueProducts.map(product => (
                            <option key={product.id} value={product.id}>
                                {product.name}
                            </option>
                        ))}
                    </select>

                    {hasActiveFilters && (
                        <button 
                            className="btn btn-secondary btn-sm reset-filters"
                            onClick={resetFilters}
                            title="Reset all filters"
                        >
                            🔄 Reset
                        </button>
                    )}

                    {hasActiveFilters && (
                        <div className="active-filters-info">
                            {reviews.length} of {allReviews.length}
                        </div>
                    )}
                </div>

                {/* Reviews Table */}
                <div className="reviews-table-container">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <LoadingState message="Loading reviews..." />
                        </div>
                    ) : reviews.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">⭐</div>
                            {hasActiveFilters || filter !== 'all' ? (
                                <>
                                    <h3>No reviews found</h3>
                                    <p>No reviews match your current filters. Try adjusting your search or filters.</p>
                                    {hasActiveFilters && (
                                        <button className="btn btn-primary" onClick={resetFilters} style={{marginTop: '16px'}}>
                                            Reset Filters
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <h3>No reviews yet</h3>
                                    <p>Reviews will appear here once customers start reviewing products</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            <table className="reviews-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort('author_name')} style={{cursor: 'pointer'}}>
                                            Author {sortBy === 'author_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th onClick={() => handleSort('rating')} style={{cursor: 'pointer'}}>
                                            Rating {sortBy === 'rating' && (sortOrder === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th>Review</th>
                                        <th>Product</th>
                                        <th onClick={() => handleSort('date')} style={{cursor: 'pointer'}}>
                                            Submitted {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedReviews.map(review => (
                                        <React.Fragment key={review.id}>
                                            <tr className="review-row">
                                                <td className="author-cell">
                                                    <div className="author-info">
                                                        <button
                                                            className="expand-btn"
                                                            onClick={() => setExpandedReview(expandedReview === review.id ? null : review.id)}
                                                        >
                                                            {expandedReview === review.id ? '−' : '+'}
                                                        </button>
                                                        <img 
                                                            src={review.author_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.author_name)}&background=6366f1&color=fff`} 
                                                            alt={review.author_name} 
                                                            className="author-avatar"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(review.author_name)}&background=6366f1&color=fff`;
                                                            }}
                                                        />
                                                        <div className="author-details">
                                                            <div className="author-name">{review.author_name}</div>
                                                            <div className="author-email">{review.author_email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="rating-cell">{renderStars(review.rating)}</td>
                                                <td className="review-cell">
                                                    <div className="review-preview">
                                                        {review.content.substring(0, 80)}...
                                                    </div>
                                                </td>
                                                <td className="product-cell">
                                                    <a href={review.product_url} target="_blank" rel="noopener noreferrer">
                                                        {review.product_name}
                                                    </a>
                                                </td>
                                                <td className="date-cell">{new Date(review.date).toLocaleDateString()}</td>
                                                <td className="actions-cell">
                                                    <div className="action-menu-wrapper">
                                                        <button
                                                            className="action-menu-btn"
                                                            onClick={() => setActionMenu(actionMenu === review.id ? null : review.id)}
                                                        >
                                                            ⋮
                                                        </button>
                                                        {actionMenu === review.id && (
                                                            <div className="action-menu">
                                                                <button onClick={() => { handleStatusChange(review.id, review.status === 'approved' ? 'hold' : 'approve'); setActionMenu(null); }}>
                                                                    {review.status === 'approved' ? 'Unapprove' : 'Approve'}
                                                                </button>
                                                                <button onClick={() => { setReplyingTo(review.id); setActionMenu(null); }}>Reply</button>
                                                                <button onClick={() => { handleStatusChange(review.id, 'spam'); setActionMenu(null); }}>Spam</button>
                                                                <div className="menu-divider"></div>
                                                                <button onClick={() => { setViewingReview(review); setActionMenu(null); }}>View</button>
                                                                <button onClick={() => { setDeleteModal(review); setActionMenu(null); }} className="danger">Trash</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedReview === review.id && (
                                                <tr className="expanded-row">
                                                    <td colSpan="6">
                                                        <div className="expanded-content">
                                                            <div className="review-full-text">{review.content}</div>
                                                            {review.verified && <span className="verified-badge">✓ Verified Owner</span>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination">
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        disabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                    >
                                        Previous
                                    </button>
                                    <span className="pagination-info">
                                        Page {page} of {totalPages}
                                    </span>
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        disabled={page === totalPages}
                                        onClick={() => setPage(page + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* View Review Modal */}
                {viewingReview && (
                    <div className="modal-overlay" onClick={() => setViewingReview(null)}>
                        <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Review Details</h3>
                                <button className="modal-close" onClick={() => setViewingReview(null)}>
                                    ✕
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="review-modal-content">
                                    {/* Rating Display - Large and Prominent */}
                                    <div className="modal-rating-section">
                                        <div className={`rating-display rating-${viewingReview.rating}`}>
                                            <div className="rating-number">{viewingReview.rating}.0</div>
                                            <div className="rating-stars">{renderStars(viewingReview.rating)}</div>
                                            <div className="rating-label">out of 5 stars</div>
                                        </div>
                                    </div>

                                    {/* Review Content */}
                                    <div className="modal-review-text">
                                        <h4>Review</h4>
                                        <p>{viewingReview.content}</p>
                                    </div>

                                    {/* Review Details */}
                                    <div className="modal-review-details">
                                        <div className="detail-item detail-item-reviewer">
                                            <img 
                                                src={viewingReview.author_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(viewingReview.author_name)}&background=6366f1&color=fff&size=80`} 
                                                alt={viewingReview.author_name}
                                                className="reviewer-avatar"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(viewingReview.author_name)}&background=6366f1&color=fff&size=80`;
                                                }}
                                            />
                                            <div className="detail-info">
                                                <div className="detail-label">Reviewer</div>
                                                <div className="detail-value">
                                                    {viewingReview.author_name}
                                                    {viewingReview.verified && (
                                                        <span className="verified-badge">✓ Verified</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">📧</span>
                                            <div className="detail-info">
                                                <div className="detail-label">Email</div>
                                                <div className="detail-value">{viewingReview.author_email}</div>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">📦</span>
                                            <div className="detail-info">
                                                <div className="detail-label">Product</div>
                                                <div className="detail-value">
                                                    <a href={viewingReview.product_url} target="_blank" rel="noopener noreferrer">
                                                        {viewingReview.product_name}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">📅</span>
                                            <div className="detail-info">
                                                <div className="detail-label">Date</div>
                                                <div className="detail-value">
                                                    {new Date(viewingReview.date).toLocaleString()}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">🏷️</span>
                                            <div className="detail-info">
                                                <div className="detail-label">Status</div>
                                                <div className="detail-value">
                                                    <span className={`status-badge status-${viewingReview.status}`}>
                                                        {viewingReview.status === 'hold' ? 'Pending' : viewingReview.status}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reply Section in Modal */}
                                    <div className="modal-reply-section">
                                        <h4>Reply to Review</h4>
                                        {replyingTo === viewingReview.id ? (
                                            <div className="reply-form">
                                                <textarea
                                                    value={replyText}
                                                    onChange={(e) => setReplyText(e.target.value)}
                                                    placeholder="Write your reply to this customer..."
                                                    rows="4"
                                                    autoFocus
                                                />
                                                <div className="reply-actions">
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => handleReply(viewingReview.id)}
                                                    >
                                                        Post Reply
                                                    </button>
                                                    <button
                                                        className="btn btn-secondary"
                                                        onClick={() => {
                                                            setReplyingTo(null);
                                                            setReplyText('');
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => setReplyingTo(viewingReview.id)}
                                            >
                                                💬 Write a Reply
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => {
                                    setViewingReview(null);
                                    setReplyingTo(null);
                                    setReplyText('');
                                }}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {deleteModal && (
                    <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Delete Review</h3>
                                <button className="modal-close" onClick={() => setDeleteModal(null)}>
                                    ✕
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to permanently delete this review?</p>
                                <div className="delete-review-info">
                                    <div className="review-rating">{renderStars(deleteModal.rating)}</div>
                                    <div><strong>{deleteModal.author_name}</strong></div>
                                    <div className="review-excerpt">{deleteModal.content.substring(0, 100)}...</div>
                                </div>
                                <p className="warning-text">⚠️ This action cannot be undone.</p>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setDeleteModal(null)}>
                                    Cancel
                                </button>
                                <button className="btn btn-danger" onClick={() => handleDelete(deleteModal.id)}>
                                    Delete Permanently
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Reviews;
